<!-- The chat bubble, draggable anywhere in the window.  Docking replaces it entirely.
	 Opening is done on pointerup rather than click, so a drag that ends over the bubble doesn't also
	 count as a click on it -- see onFabDragEnd. -->
<div *ngIf="!isDocked" class="oc-fab" [class.oc-fab-open]="isFabOpen" [class.oc-fab-unread]="unreadCount > 0" [class.oc-fab-dragging]="isDraggingFab"
	 [class.oc-fab-pinned-left]="pinnedSide === 'left'" [class.oc-fab-pinned-right]="pinnedSide === 'right'"
	 [style.left]="fabStyle.left" [style.top]="fabStyle.top" (pointerdown)="startFabDrag($event)"
	 title="Team Chat - drag to move, or drop it against a side to park it there">
	<i class="far" [ngClass]="isFabOpen ? 'fa-times' : 'fa-comments'"></i>
	<span *ngIf="unreadCount > 0 && !isFabOpen" class="oc-badge">{{badgeText(unreadCount)}}</span>
</div>

<!-- Flyout hanging off the bubble, wherever the bubble has been dragged to.  Created on open and
	 destroyed on close, which is what lets the boing animation fire every time rather than only on
	 first paint. -->
<div *ngIf="!isDocked && isFabOpen" class="oc-flyout" [class.oc-flyout-dragging]="isDraggingFab"
	 [style.left]="flyoutStyle.left" [style.top]="flyoutStyle.top" [style.height]="flyoutStyle.height" [style.transform-origin]="flyoutStyle.transformOrigin">
	<ng-container *ngTemplateOutlet="panel"></ng-container>
</div>

<!-- Docked to the right edge.  The page shrinks to make room -- see --team-chat-dock-width. -->
<div *ngIf="isDocked && !isCollapsed" class="oc-dock">
	<ng-container *ngTemplateOutlet="panel"></ng-container>
</div>

<!-- Collapse handle: a circle sitting a short way down the docked panel's edge. -->
<div *ngIf="isDocked" class="oc-handle" [class.oc-handle-collapsed]="isCollapsed" (click)="toggleCollapsed()"
	 [title]="isCollapsed ? 'Show team chat' : 'Collapse team chat'">
	<i class="far" [ngClass]="isCollapsed ? 'fa-comments' : 'fa-chevron-right'"></i>
	<span *ngIf="isCollapsed && unreadCount > 0" class="oc-badge oc-badge-handle">{{badgeText(unreadCount)}}</span>
</div>

<ng-template #panel>
	<div class="oc-panel">
		<!-- ---------------- Header ---------------- -->
		<div class="oc-header">
			<div *ngIf="view !== 'list'" class="oc-header_back" (click)="view === 'members' ? cancelMembers() : showList()" title="Back">
				<i class="far fa-chevron-left"></i>
			</div>

			<div class="oc-header_title">
				<ng-container *ngIf="view === 'list'">Team Chat</ng-container>
				<ng-container *ngIf="view === 'members'">{{ membersForConversationId ? 'Chat members' : 'New chat' }}</ng-container>

				<ng-container *ngIf="view === 'chat' && activeConversation">
					<div *ngIf="!isRenaming" class="oc-header_name" (click)="startRename()" [title]="getDisplayName(activeConversation) + ' - click to rename this chat'">
						<!-- Who's in the chat, as faces rather than a run of comma-separated names.  A chat
							 built from teams is titled by those instead -- see showHeaderFaces. -->
						<div *ngIf="showHeaderFaces" class="oc-header_faces">
							<div *ngFor="let member of headerMembers" class="oc-avatar oc-avatar-sm oc-header_face" [title]="member.userFullName">
								<img *ngIf="member.profilePictureUrl" [src]="member.profilePictureUrl" />
								<span *ngIf="!member.profilePictureUrl">{{getInitials(member.userFullName)}}</span>
							</div>
							<div *ngIf="hiddenMemberCount > 0" class="oc-avatar oc-avatar-sm oc-header_face" [title]="getDisplayName(activeConversation)">+{{hiddenMemberCount}}</div>
						</div>

						<!-- A chat somebody has named keeps showing that name, and one built from teams shows
							 those; anything else is just its faces, unless there are none to show (a chat
							 with only you in it). -->
						<span *ngIf="!showHeaderFaces" class="oc-ellipsis">{{getDisplayName(activeConversation)}}</span>

						<i class="far fa-pencil ms-1"></i>
					</div>
					<input *ngIf="isRenaming"
						   type="text"
						   class="form-control form-control-sm oc-rename"
						   maxlength="100"
						   placeholder="Name this chat..."
						   [(ngModel)]="renameText"
						   (keydown)="onRenameKeyDown($event)"
						   (blur)="saveRename()"
						   autofocus />
				</ng-container>
			</div>

			<div *ngIf="view === 'chat' && activeConversation" class="oc-header_button" (click)="showEditMembers()" title="Add or remove people">
				<i class="far fa-user-plus"></i>
			</div>

			<!-- On an open chat the bell is that one chat's mute switch and nothing more: one click, no
			     popover behind it.  It is the same bell the chat carries in the list, in the same place a
			     chat's own buttons sit. -->
			<div *ngIf="view === 'chat' && activeConversation" class="oc-header_button"
			     [class.oc-header_button-on]="isActiveConversationMuted" (click)="toggleActiveMute()"
			     [title]="isActiveConversationMuted ? 'Unmute this chat' : 'Mute this chat'">
				<i class="far" [ngClass]="isActiveConversationMuted ? 'fa-bell-slash' : 'fa-bell'"></i>
			</div>

			<!-- On the list it is a speaker instead, opening the sound settings.  How loud the chime is
			     belongs to the widget rather than to any one chat, so it is reached from here and nowhere
			     else -- and a speaker rather than a bell because there is a level behind this one, which the
			     icon shows roughly without anybody having to open it.  A click anywhere outside puts the
			     popover away via onDocumentClick, and clicks inside it are stopped so they do not. -->
			<div *ngIf="view === 'list'" class="oc-header_button oc-volume"
			     [class.oc-header_button-on]="isNotificationMuted" (click)="toggleVolumePopover($event)"
			     [title]="volumeButtonTitle">
				<i class="far" [ngClass]="volumeIconClass"></i>
				<ng-container *ngIf="isVolumePopoverOpen" [ngTemplateOutlet]="volumePopover"></ng-container>
			</div>

			<div *ngIf="!isDocked" class="oc-header_button" (click)="dock()" title="Dock to the right side">
				<i class="far fa-arrow-right-to-line"></i>
			</div>
			<div *ngIf="isDocked" class="oc-header_button" (click)="undock()" title="Undock">
				<i class="far fa-arrow-left-from-line"></i>
			</div>
			<div *ngIf="!isDocked" class="oc-header_button" (click)="closeFab()" title="Close">
				<i class="far fa-times"></i>
			</div>
		</div>

		<!-- ---------------- Conversation list ---------------- -->
		<div *ngIf="view === 'list'" class="oc-body">
			<div *ngIf="conversations.length === 0" class="oc-empty">
				<i class="far fa-comments"></i>
				<div>No chats yet</div>
			</div>

			<div *ngFor="let conversation of conversations" class="oc-conv" (click)="selectConversation(conversation)">
				<div class="oc-conv_body">
					<div class="oc-conv_head">
						<span class="oc-conv_name oc-ellipsis">{{getDisplayName(conversation)}}</span>
						<span *ngIf="conversation.lastMessageDate" class="oc-conv_time">{{conversation.lastMessageDate | date: 'M/d/yy h:mm a'}}</span>
					</div>
					<div class="oc-conv_preview oc-ellipsis">
						<span *ngIf="conversation.lastMessageSenderFullName">{{conversation.lastMessageSenderFullName}}: </span>{{conversation.lastMessageBody}}
					</div>
				</div>

				<div class="oc-conv_right">
					<!-- A muted chat never badges, so the crossed bell is the only thing marking it. -->
					<span *ngIf="conversation.unreadMessages > 0 && !conversation.isMuted" class="oc-badge oc-badge-inline">{{badgeText(conversation.unreadMessages)}}</span>
					<span class="oc-conv_icon" [class.oc-conv_icon-on]="conversation.isMuted" (click)="toggleMute($event, conversation)"
						  [title]="conversation.isMuted ? 'Unmute this chat' : 'Mute this chat'">
						<i class="far" [ngClass]="conversation.isMuted ? 'fa-bell-slash' : 'fa-bell'"></i>
					</span>
					<span class="oc-conv_close" (click)="closeConversation($event, conversation)" title="Close this chat">
						<i class="far fa-times"></i>
					</span>
				</div>
			</div>
		</div>

		<div *ngIf="view === 'list'" class="oc-footer">
			<button type="button" class="btn btn-primary btn-sm w-100" (click)="showNewChat()">
				<i class="far fa-plus"></i> New chat
			</button>
		</div>

		<!-- ---------------- Messages ---------------- -->
		<!-- The inner wrapper is what lets the thread sit against the bottom and grow upwards -- see
			 .oc-messages_inner.  It can't be done on the scroller itself without risking the top of a
			 long thread becoming unreachable. -->
		<div *ngIf="view === 'chat'" #messagesRef class="oc-body oc-messages" (scroll)="onMessagesScroll()">
		  <div class="oc-messages_inner" [class.oc-messages-settling]="!isThreadReady">
			<div *ngIf="teamChatStore.isLoadingOlderMessages" class="oc-loading-more">Loading earlier messages...</div>

			<div *ngIf="!activeConversation" class="oc-empty">Loading...</div>

			<ng-container *ngIf="activeConversation">
				<div *ngIf="activeConversation.messages.length === 0" class="oc-empty">
					<i class="far fa-comment-dots"></i>
					<div>Say something to get started</div>
				</div>

				<div *ngFor="let message of activeConversation.messages">
					<!-- "Scott added Bob", renames, etc. -->
					<div *ngIf="message.isSystemMessage" class="oc-system">
						{{message.sentDate | date: 'M/d/yy h:mm a'}} - {{message.body}}
					</div>

					<div *ngIf="!message.isSystemMessage" class="oc-message" [class.oc-message-mine]="message.senderId === userId">
						<div class="oc-avatar" [title]="message.senderFullName">
							<img *ngIf="message.senderProfilePictureUrl" [src]="message.senderProfilePictureUrl" />
							<span *ngIf="!message.senderProfilePictureUrl">{{getInitials(message.senderFullName)}}</span>
						</div>

						<div class="oc-bubble" [ngClass]="message.senderId === userId ? 'oc-bubble-mine' : 'oc-bubble-theirs'">
							<div class="oc-bubble_sender" *ngIf="message.senderId !== userId">{{message.senderFullName}}</div>
							<!-- Split so anybody @mentioned stands out from the rest of the sentence. -->
							<div class="oc-bubble_body" *ngIf="message.body"><ng-container *ngFor="let segment of message.body | chatMentions"><span *ngIf="segment.isMention" class="oc-mention">{{segment.text}}</span><a *ngIf="segment.isLink" class="oc-link" [href]="segment.url" target="_blank" rel="noopener noreferrer">{{segment.text}}</a><ng-container *ngIf="!segment.isMention && !segment.isLink">{{segment.text}}</ng-container></ng-container></div>

							<!-- Photos show themselves; anything else is a link out to the file. -->
							<!-- width/height are the sender's recorded pixel size.  The browser reserves the box from
								 them (constrained by .oc-bubble_image's max-width/max-height, exactly as the loaded
								 picture will be), so nothing below it moves when the photo arrives.  Omitted when the
								 size wasn't recorded, which is every message sent before it was captured. -->
							<img *ngIf="message.attachmentUrl && isImage(message)"
								 class="oc-bubble_image"
								 [src]="message.attachmentUrl"
								 [attr.width]="message.attachmentWidth || null"
								 [attr.height]="message.attachmentHeight || null"
								 [alt]="message.attachmentFileName || 'Photo'"
								 (load)="onAttachmentLoaded()"
								 (click)="openAttachment(message)" />

							<div *ngIf="message.attachmentUrl && !isImage(message)" class="oc-bubble_file" (click)="openAttachment(message)"
								 [title]="message.attachmentFileName">
								<i class="far fa-paperclip"></i>
								<span class="oc-ellipsis">{{message.attachmentFileName || 'Attachment'}}</span>
							</div>

							<div class="oc-bubble_time">{{message.sentDate | date: 'M/d/yy h:mm a'}}</div>
						</div>
					</div>
				</div>
			</ng-container>
		  </div>
		</div>

		<div *ngIf="view === 'chat' && activeConversation" class="oc-footer oc-composer">
			<!-- Sits above the composer and closes on the next click anywhere else -- see onDocumentClick. -->
			<div *ngIf="isEmojiPickerOpen" class="oc-emoji-panel" (click)="$event.stopPropagation()">
				<emoji-picker (emojiSelected)="insertEmoji($event)"></emoji-picker>
			</div>

			<!-- "@" autocomplete.  Anybody in the company can be tagged, whether or not they're in this
				 chat -- tagging somebody who isn't adds them. -->
			<div *ngIf="mentions.isOpen" class="oc-mention-list" (click)="$event.stopPropagation()">
				<div *ngFor="let user of mentions.users; let i = index"
					 class="oc-mention-list_user"
					 [class.oc-mention-list_user-selected]="i === mentions.selectedIndex"
					 (mousedown)="selectMention($event, user)">
					<div class="oc-avatar oc-avatar-sm">
						<img *ngIf="user.profilePictureUrl" [src]="user.profilePictureUrl" />
						<span *ngIf="!user.profilePictureUrl">{{getInitials(user.userFullName)}}</span>
					</div>
					<span class="oc-ellipsis">{{user.userFullName}}</span>
					<span *ngIf="!isMember(user)" class="oc-mention-list_add" title="Not in this chat yet - tagging them adds them">
						<i class="far fa-user-plus"></i>
					</span>
				</div>
			</div>

			<!-- ":" autocomplete: type the name of the one you want -- ":wink" -- instead of going
				 hunting for it in the picker.  Closing the name off (":wink:") skips the list entirely. -->
			<div *ngIf="emojis.isOpen" class="oc-mention-list" (click)="$event.stopPropagation()">
				<div *ngFor="let shortcode of emojis.matches; let i = index"
					 class="oc-mention-list_user"
					 [class.oc-mention-list_user-selected]="i === emojis.selectedIndex"
					 (mousedown)="selectEmoji($event, shortcode)">
					<span class="oc-emoji-suggestion">{{shortcode.emoji}}</span>
					<span class="oc-ellipsis">:{{shortcode.names[0]}}:</span>
				</div>
			</div>

			<button type="button" class="oc-composer_button" title="Emoji" (click)="toggleEmojiPicker($event)">
				<i class="far fa-face-smile"></i>
			</button>

			<button type="button" class="oc-composer_button" title="Send a photo or document" [disabled]="isUploading" (click)="pickFiles()">
				<i class="far" [ngClass]="isUploading ? 'fa-spinner fa-spin' : 'fa-paperclip'"></i>
			</button>

			<!-- Hidden: the paperclip is what the user actually clicks. -->
			<input #fileInputRef type="file" multiple class="d-none" (change)="onFilesPicked($event)" />

			<textarea #newMessageRef
					  class="form-control form-control-sm"
					  rows="1"
					  placeholder="Message..."
					  [(ngModel)]="newMessage"
					  (input)="onComposerChanged()"
					  (click)="onComposerChanged()"
					  (keydown)="onKeyDown($event)"></textarea>
			<button type="button" class="btn btn-primary btn-sm ms-1" [disabled]="!newMessage" allowDoubleClick (click)="sendMessage()">
				<i class="far fa-paper-plane"></i>
			</button>
		</div>

		<!-- ---------------- New chat / members ---------------- -->
		<div *ngIf="view === 'members'" class="oc-body">
			<div class="oc-members_name">
				<input type="text"
					   class="form-control form-control-sm"
					   maxlength="100"
					   placeholder="Chat name (optional)"
					   [(ngModel)]="newChatName" />
			</div>

			<div class="oc-members_search input-group input-group-sm">
				<span class="input-group-text"><i class="far fa-search"></i></span>
				<input type="text" class="form-control" placeholder="Find teams or people..." autocomplete="off" [(ngModel)]="memberSearchText" />
			</div>

			<div *ngIf="hasNoPickerResults" class="oc-empty">Nothing found</div>

			<!-- What the chat already is, before what could be added to it.  Only on a chat that exists:
				 a new one has nothing in it yet, and the section stays away rather than sitting empty. -->
			<ng-container *ngIf="filteredCurrentTeams.length > 0 || filteredCurrentUsers.length > 0">
				<div class="oc-members_heading" (click)="toggleCurrentCollapsed()"
					 [title]="isCurrentCollapsed ? 'Show who is in this chat' : 'Hide who is in this chat'">
					<i class="far" [ngClass]="isCurrentCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
					<span>In this chat</span>
					<span class="oc-members_heading_count">{{filteredCurrentTeams.length + filteredCurrentUsers.length}}</span>
				</div>

				<ng-container *ngIf="!isCurrentCollapsed">
					<ng-container *ngFor="let team of filteredCurrentTeams">
						<ng-container *ngTemplateOutlet="teamRow; context: { $implicit: team }"></ng-container>
					</ng-container>
					<ng-container *ngFor="let user of filteredCurrentUsers">
						<ng-container *ngTemplateOutlet="userRow; context: { $implicit: user }"></ng-container>
					</ng-container>
				</ng-container>
			</ng-container>

			<!-- Teams lead the rest: one tick puts a whole crew in the chat and keeps it in step with
				 the team, which is a different thing from ticking the same names one at a time today. -->
			<div *ngIf="filteredTeams.length > 0" class="oc-members_heading" (click)="toggleTeamsCollapsed()"
				 [title]="isTeamsCollapsed ? 'Show teams' : 'Hide teams'">
				<i class="far" [ngClass]="isTeamsCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
				<span>Teams</span>
				<!-- The count carries the folded list: with the section rolled up it's the only thing
					 saying whether a search found anything in there. -->
				<span class="oc-members_heading_count">{{filteredTeams.length}}</span>
			</div>

			<ng-container *ngIf="!isTeamsCollapsed">
				<ng-container *ngFor="let team of filteredTeams">
					<ng-container *ngTemplateOutlet="teamRow; context: { $implicit: team }"></ng-container>
				</ng-container>
			</ng-container>

			<div *ngIf="filteredUsers.length > 0" class="oc-members_heading" (click)="togglePeopleCollapsed()"
				 [title]="isPeopleCollapsed ? 'Show people' : 'Hide people'">
				<i class="far" [ngClass]="isPeopleCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
				<span>People</span>
				<span class="oc-members_heading_count">{{filteredUsers.length}}</span>
			</div>

			<ng-container *ngIf="!isPeopleCollapsed">
				<ng-container *ngFor="let user of filteredUsers">
					<ng-container *ngTemplateOutlet="userRow; context: { $implicit: user }"></ng-container>
				</ng-container>
			</ng-container>

			<!-- One row apiece, drawn wherever they're listed: a team on the chat and a team that could
				 be added are the same row with the same tick, and they behave the same way. -->
			<ng-template #teamRow let-team>
				<div class="oc-user" [class.oc-user-selected]="isTeamSelected(team)" (click)="toggleTeam(team)"
					 [title]="team.teamName + ' - everyone on this team, and whoever joins it later'">
					<i class="far" [ngClass]="isTeamSelected(team) ? 'fa-check-square' : 'fa-square'"></i>
					<div class="oc-avatar oc-avatar-sm oc-avatar-team"><i class="far fa-users"></i></div>
					<span class="oc-ellipsis">{{team.teamName}}</span>
					<span class="oc-user_note">{{team.memberUserIds.length}}</span>
				</div>
			</ng-template>

			<!-- Somebody a ticked team already covers is in the chat either way.  Ticking them as well
				 is allowed and does nothing today -- it pins them, so they keep the chat if they come
				 off the team.  Either way they're one member who gets one copy of each message. -->
			<ng-template #userRow let-user>
				<div class="oc-user" [class.oc-user-selected]="isSelected(user)" [class.oc-user-covered]="isCoveredByTeam(user) && !isSelected(user)"
					 (click)="toggleUser(user)"
					 [title]="isCoveredByTeam(user) ? user.userFullName + ' is in through ' + coveringTeamNames(user) + ' - tick them to keep them in this chat even if they leave the team' : user.userFullName">
					<i class="far" [ngClass]="isSelected(user) ? 'fa-check-square' : (isCoveredByTeam(user) ? 'fa-users' : 'fa-square')"></i>
					<div class="oc-avatar oc-avatar-sm">
						<img *ngIf="user.profilePictureUrl" [src]="user.profilePictureUrl" />
						<span *ngIf="!user.profilePictureUrl">{{getInitials(user.userFullName)}}</span>
					</div>
					<span class="oc-ellipsis">{{user.userFullName}}</span>
					<span *ngIf="isCoveredByTeam(user)" class="oc-user_note oc-ellipsis">{{coveringTeamNames(user)}}</span>
				</div>
			</ng-template>
		</div>

		<div *ngIf="view === 'members'" class="oc-footer">
			<button type="button" class="btn btn-primary btn-sm flex-fill me-1" [disabled]="!canSaveMembers" (click)="saveMembers()">
				<i class="far fa-check"></i> {{ membersForConversationId ? 'Save' : 'Start chat' }}
			</button>
			<button type="button" class="btn btn-outline-secondary btn-sm flex-fill" (click)="cancelMembers()">
				<i class="far fa-ban"></i> Cancel
			</button>
		</div>
	</div>
</ng-template>


<!-- Hangs off the bell in the list header.  Sits inside the panel (the flyout clips anything that does
     not), so it is right-aligned under the button and kept narrow. -->
<ng-template #volumePopover>
	<div class="oc-volume_popover" (click)="onVolumePopoverClick($event)">
		<!-- One level for the whole widget: every chat is heard at it, and muting here silences the lot
		     without touching which individual chats the user has muted. -->
		<div class="oc-volume_row">
			<span class="oc-volume_mute" [class.oc-volume_mute-on]="isNotificationMuted" (click)="toggleNotificationMute($event)"
			      [title]="isNotificationMuted ? 'Unmute' : 'Mute'">
				<i class="far" [ngClass]="isNotificationMuted ? 'fa-volume-slash' : 'fa-volume-up'"></i>
			</span>

			<!-- Left enabled while muted on purpose: dragging it up is the other way back to sound. -->
			<input type="range" class="oc-volume_slider" min="0" max="100" step="1"
			       [value]="notificationVolumePercent" (input)="onNotificationVolumeChange($event)"
			       title="Notification volume" />

			<span class="oc-volume_value">{{isNotificationMuted ? 'Muted' : notificationVolumePercent + '%'}}</span>
		</div>
	</div>
</ng-template>
